import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, BarChart2 } from 'lucide-react';
import { Card, StatCard } from '@/components/Card';
import { useApi } from '@/hooks/useApi';
import { post, patch, del } from '@/lib/api';

interface SalaryBand {
  id: string;
  name: string;
  job_family?: string;
  level: number;
  min_salary: number;
  midpoint_salary: number;
  max_salary: number;
  currency: string;
  effective_date: string;
}

interface BandAnalysis {
  band_id: string;
  band_name: string;
  min_salary: number;
  midpoint_salary: number;
  max_salary: number;
  currency: string;
  employee_count: number;
  in_range: number;
  below_range: number;
  above_range: number;
  employees_below: Array<{ id: string; name: string; salary?: number }>;
  employees_above: Array<{ id: string; name: string; salary?: number }>;
}

const EMPTY_FORM = {
  name: '',
  job_family: '',
  level: 1,
  min_salary: 60000,
  midpoint_salary: 80000,
  max_salary: 100000,
  currency: 'USD',
  effective_date: new Date().toISOString().split('T')[0],
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

function BandRangeBar({ band }: { band: SalaryBand | BandAnalysis }) {
  const range = band.max_salary - band.min_salary;
  if (range <= 0) return null;
  const midPct = ((band.midpoint_salary - band.min_salary) / range) * 100;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>{formatCurrency(band.min_salary, band.currency)}</span>
        <span className="text-amber-400">{formatCurrency(band.midpoint_salary, band.currency)}</span>
        <span>{formatCurrency(band.max_salary, band.currency)}</span>
      </div>
      <div className="relative h-3 bg-surface-3 rounded-full overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500/40 to-green-500/40 rounded-full" style={{ width: '100%' }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-amber-400" style={{ left: `${midPct}%` }} />
      </div>
    </div>
  );
}

export function SalaryBandsAdminPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const { data, refetch } = useApi<{ bands: SalaryBand[] }>('admin/salary-bands');
  const { data: analysisData } = useApi<{ analysis: BandAnalysis[] }>(showAnalysis ? 'admin/salary-bands/analysis' : '', [showAnalysis]);

  const bands = data?.bands ?? [];
  const analysis = analysisData?.analysis ?? [];

  const totalOutOfRange = analysis.reduce((sum, b) => sum + b.below_range + b.above_range, 0);
  const totalAssigned = analysis.reduce((sum, b) => sum + b.employee_count, 0);

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(b: SalaryBand) {
    setForm({
      name: b.name,
      job_family: b.job_family ?? '',
      level: b.level,
      min_salary: b.min_salary,
      midpoint_salary: b.midpoint_salary,
      max_salary: b.max_salary,
      currency: b.currency,
      effective_date: b.effective_date?.split('T')[0] ?? new Date().toISOString().split('T')[0],
    });
    setEditingId(b.id);
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = { ...form, job_family: form.job_family || undefined };
      if (editingId) {
        await patch(`admin/salary-bands/${editingId}`, payload);
      } else {
        await post('admin/salary-bands', payload);
      }
      setShowForm(false);
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete salary band "${name}"?`)) return;
    await del(`admin/salary-bands/${id}`);
    refetch();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground">Salary Bands</h2>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setShowAnalysis(!showAnalysis)} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${showAnalysis ? 'bg-amber-500/20 text-amber-400' : 'bg-surface-2 text-muted-foreground hover:text-foreground'}`}>
            <BarChart2 className="w-4 h-4" /> Analysis
          </button>
          <button onClick={openCreate} className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-sm font-medium rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> New Band
          </button>
        </div>
      </div>

      {showAnalysis && analysis.length > 0 && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <StatCard label="Total Bands" value={bands.length} icon={<BarChart2 className="w-6 h-6" />} />
            <StatCard label="Employees Assigned" value={totalAssigned} />
            <StatCard label="Out of Range" value={totalOutOfRange} accent={totalOutOfRange > 0} />
          </div>
          <div className="space-y-3">
            {analysis.map(b => (
              <Card key={b.band_id}>
                <div className="flex items-start justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{b.band_name}</span>
                  <div className="flex gap-3 text-xs">
                    <span className="text-muted-foreground">{b.employee_count} emp</span>
                    {b.in_range > 0 && <span className="text-green-400">{b.in_range} in range</span>}
                    {b.below_range > 0 && <span className="text-red-400">{b.below_range} below</span>}
                    {b.above_range > 0 && <span className="text-yellow-400">{b.above_range} above</span>}
                  </div>
                </div>
                <BandRangeBar band={b} />
                {(b.employees_below.length > 0 || b.employees_above.length > 0) && (
                  <div className="mt-2 text-xs space-y-0.5">
                    {b.employees_below.map(e => <div key={e.id} className="text-red-400">{e.name} — {e.salary ? formatCurrency(e.salary, b.currency) : 'N/A'} (below min)</div>)}
                    {b.employees_above.map(e => <div key={e.id} className="text-yellow-400">{e.name} — {e.salary ? formatCurrency(e.salary, b.currency) : 'N/A'} (above max)</div>)}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <Card className="border border-amber-500/30">
          <h3 className="text-sm font-semibold text-foreground mb-4">{editingId ? 'Edit Salary Band' : 'New Salary Band'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Band Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="Engineer L2" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Job Family</label>
              <input value={form.job_family} onChange={e => setForm(f => ({ ...f, job_family: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="Engineering" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Level</label>
              <input type="number" value={form.level} onChange={e => setForm(f => ({ ...f, level: parseInt(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" min={1} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Currency</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Minimum Salary *</label>
              <input type="number" value={form.min_salary} onChange={e => setForm(f => ({ ...f, min_salary: parseFloat(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Midpoint Salary *</label>
              <input type="number" value={form.midpoint_salary} onChange={e => setForm(f => ({ ...f, midpoint_salary: parseFloat(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Maximum Salary *</label>
              <input type="number" value={form.max_salary} onChange={e => setForm(f => ({ ...f, max_salary: parseFloat(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Effective Date</label>
              <input type="date" value={form.effective_date} onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
          </div>
          {form.min_salary > 0 && form.midpoint_salary > 0 && form.max_salary > 0 && (
            <div className="mt-4 p-3 bg-surface-3 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Preview</p>
              <BandRangeBar band={{ ...form, id: '', tenant_id: '', type: 'salary_band', created_at: '', updated_at: '' } as SalaryBand} />
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} disabled={saving || !form.name || form.min_salary <= 0} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black text-sm font-medium rounded-lg transition-colors">
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-surface-2 hover:bg-surface-3 text-foreground text-sm rounded-lg transition-colors">Cancel</button>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {bands.length === 0 && !showForm && (
          <div className="text-center py-12 text-muted-foreground text-sm">No salary bands yet. Create bands to define compensation ranges.</div>
        )}
        {[...bands].sort((a, b) => a.level - b.level).map(b => (
          <Card key={b.id}>
            <div className="flex items-start justify-between">
              <div className="flex-1 mr-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{b.name}</span>
                  {b.job_family && <span className="text-xs text-muted-foreground">{b.job_family}</span>}
                  <span className="text-xs px-1.5 py-0.5 rounded bg-surface-3 text-muted-foreground">L{b.level}</span>
                  <span className="text-xs text-muted-foreground">{b.currency}</span>
                </div>
                <BandRangeBar band={b} />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => openEdit(b)} className="text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(b.id, b.name)} className="text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
